import { type SizeSettingSetMember } from '@air/api';
import {
  ALL_SOCIAL_MEDIA_FORMAT_IDS,
  getSizeFormatId,
  isSavableSize,
  type SavedSize,
  type SaveSetMemberInput,
  type SizeSet,
  SOCIAL_MEDIA_PLATFORM_GROUPS,
} from '@air/feature-saved-sizes/utils';

import {
  WORKSPACE_AGENT_SMART_RESIZE_CHANNELS,
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES,
  type WorkspaceAgentSmartResizeRatioSize,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeSize,
  type WorkspaceAgentSmartResizeSizeSet,
  workspaceAgentSmartResizeChannelToSize,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentSmartResizeSavedCatalogSaveParams = {
  name: string;
  sizes: readonly WorkspaceAgentSmartResizeSize[];
};

export type WorkspaceAgentSmartResizeSavedCatalogSave = (
  params: WorkspaceAgentSmartResizeSavedCatalogSaveParams,
) => Promise<boolean>;

export type WorkspaceAgentSmartResizeSavedCatalog = {
  isLoading: boolean;
  onSave: WorkspaceAgentSmartResizeSavedCatalogSave | undefined;
  sets: readonly WorkspaceAgentSmartResizeSizeSet[];
  sizes: readonly WorkspaceAgentSmartResizeSavedSize[];
};

export const EMPTY_WORKSPACE_AGENT_SMART_RESIZE_SAVED_CATALOG: WorkspaceAgentSmartResizeSavedCatalog = {
  isLoading: false,
  onSave: undefined,
  sets: [],
  sizes: [],
};

const INTEGER_RATIO_PATTERN = /^(\d+):(\d+)$/;

const platformRatioByKey = new Map(
  SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) => group.formats.map((format) => [format.id, format.ratio])),
);

const channelById = new Map(WORKSPACE_AGENT_SMART_RESIZE_CHANNELS.map((channel) => [channel.id, channel]));

const exactShapeById = new Map(
  WORKSPACE_AGENT_SMART_RESIZE_SHAPES.flatMap((shape) =>
    shape.size.by === 'exact-size' ? [[shape.id, shape.size] as const] : [],
  ),
);

const greatestCommonDivisor = (left: number, right: number): number =>
  right === 0 ? left : greatestCommonDivisor(right, left % right);

/**
 * Workspace presets store pixels, and platform members store a ratio key. A bare ratio from this
 * control matches neither, so it cannot be written back.
 */
export const canSaveWorkspaceAgentSmartResizeSelection = (sizes: readonly WorkspaceAgentSmartResizeSize[]): boolean =>
  sizes.length > 0 &&
  sizes.every((size) => size.by === 'exact-size' && isSavableSize({ height: size.height, width: size.width }));

export const workspaceAgentSmartResizeSavedSizeFromPreset = (
  preset: SavedSize,
): WorkspaceAgentSmartResizeSavedSize => ({
  id: preset.id,
  name: preset.name,
  size: {
    by: 'exact-size',
    height: preset.height,
    label: preset.name,
    width: preset.width,
  },
});

const ratioSizeFromPlatformRatio = (ratio: string): WorkspaceAgentSmartResizeRatioSize | null => {
  const match = INTEGER_RATIO_PATTERN.exec(ratio);

  if (!match?.[1] || !match[2]) {
    return null;
  }

  const width = Number(match[1]);
  const height = Number(match[2]);

  if (!width || !height) {
    return null;
  }

  const divisor = greatestCommonDivisor(width, height);

  return { by: 'ratio', height: height / divisor, width: width / divisor };
};

const sizeFromSetMember = (
  member: SizeSettingSetMember,
  presetsById: ReadonlyMap<string, SavedSize>,
): WorkspaceAgentSmartResizeSize | null => {
  if (member.type === 'sizePreset') {
    const preset = presetsById.get(member.sizePresetId);

    return preset ? workspaceAgentSmartResizeSavedSizeFromPreset(preset).size : null;
  }

  const channel = channelById.get(member.platformKey);

  if (channel) {
    return workspaceAgentSmartResizeChannelToSize(channel);
  }

  const exactShape = exactShapeById.get(member.platformKey);

  if (exactShape) {
    return exactShape;
  }

  const ratio = platformRatioByKey.get(member.platformKey);

  return ratio ? ratioSizeFromPlatformRatio(ratio) : null;
};

export const workspaceAgentSmartResizeSizeSetsFromCatalog = (
  sizeSets: readonly SizeSet[],
  presets: readonly SavedSize[],
): WorkspaceAgentSmartResizeSizeSet[] => {
  const presetsById = new Map(presets.map((preset) => [preset.id, preset]));

  return sizeSets.flatMap((sizeSet) => {
    const sizes = sizeSet.members.flatMap((member) => {
      const size = sizeFromSetMember(member, presetsById);

      return size ? [size] : [];
    });

    if (sizes.length === 0) {
      return [];
    }

    return [{ id: sizeSet.id, name: sizeSet.name, sizes }];
  });
};

/** Null when any selected size cannot be stored. Callers must not drop those sizes and save the rest. */
export const workspaceAgentSmartResizeSaveMembers = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  presets: readonly SavedSize[],
): SaveSetMemberInput[] | null => {
  if (!canSaveWorkspaceAgentSmartResizeSelection(sizes)) {
    return null;
  }

  const members: SaveSetMemberInput[] = [];
  const seenDimensions = new Set<string>();
  const seenPlatformKeys = new Set<string>();

  for (const size of sizes) {
    if (size.by !== 'exact-size') {
      return null;
    }

    if (size.formatId && ALL_SOCIAL_MEDIA_FORMAT_IDS.has(size.formatId)) {
      if (seenPlatformKeys.has(size.formatId)) {
        continue;
      }

      seenPlatformKeys.add(size.formatId);
      members.push({ platformKey: size.formatId, type: 'platform' });
      continue;
    }

    const dimensionsKey = `${size.width}x${size.height}`;

    if (seenDimensions.has(dimensionsKey)) {
      continue;
    }

    seenDimensions.add(dimensionsKey);

    const preset = presets.find((candidate) => candidate.width === size.width && candidate.height === size.height);

    if (preset) {
      members.push({ sizePresetId: preset.id, type: 'sizePreset' });
      continue;
    }

    members.push({
      customSize: {
        height: size.height,
        id: getSizeFormatId({ height: size.height, width: size.width }),
        width: size.width,
      },
      type: 'customSize',
    });
  }

  return members;
};
